<template>
  <div class="followupbot-campaigns">
    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <div class="header-section mb-6">
        <div class="header-content">
          <h1 class="page-title">
            <v-icon large class="mr-3" color="#667eea">mdi-bullhorn</v-icon>
            Campaign Management
          </h1>
          <v-btn
            color="primary"
            large
            elevation="2"
            @click="showCreateDialog = true"
            class="create-btn"
          >
            <v-icon left>mdi-plus</v-icon>
            Create New Campaign
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <v-card class="filters-card mb-6" elevation="4">
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search campaigns..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                @input="applyFilters"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                label="Status"
                :items="statusOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.deal_stage"
                label="Deal Stage"
                :items="dealStageOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.engagement_level"
                label="Engagement Level"
                :items="engagementOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Campaigns Table -->
      <v-card elevation="4">
        <v-data-table
          :headers="headers"
          :items="campaigns"
          :loading="isLoading"
          :search="filters.search"
          class="campaigns-table"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 25, 50]
          }"
        >
          <!-- Contact Info -->
          <template v-slot:[`item.contact_info`]="{ item }">
            <div class="contact-info">
              <div class="contact-avatar">
                {{ item.contact_name.charAt(0).toUpperCase() }}
              </div>
              <div class="contact-details">
                <div class="contact-name">{{ item.contact_name }}</div>
                <div class="contact-company">{{ item.contact_company }}</div>
              </div>
            </div>
          </template>

          <!-- Deal Stage -->
          <template v-slot:[`item.deal_stage`]="{ item }">
            <v-chip
              :color="getDealStageColor(item.deal_stage)"
              text-color="white"
              small
              class="stage-chip"
            >
              {{ getDealStageText(item.deal_stage) }}
            </v-chip>
          </template>

          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
              class="status-chip"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <!-- Engagement Level -->
          <template v-slot:[`item.engagement_level`]="{ item }">
            <div class="engagement-badge">
              <v-icon :color="getEngagementColor(item.engagement_level)" class="mr-1">
                {{ getEngagementIcon(item.engagement_level) }}
              </v-icon>
              {{ getEngagementText(item.engagement_level) }}
            </div>
          </template>

          <!-- Follow-ups -->
          <template v-slot:[`item.follow_ups`]="{ item }">
            <div class="follow-up-stats">
              <div class="stat-item">
                <span class="stat-value">{{ item.total_follow_ups_sent || 0 }}</span>
                <span class="stat-label">Sent</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ item.replies_received || 0 }}</span>
                <span class="stat-label">Replies</span>
              </div>
            </div>
          </template>

          <!-- Next Follow-up -->
          <template v-slot:[`item.next_follow_up_date`]="{ item }">
            <div class="next-followup">
              {{ formatNextFollowUp(item.next_follow_up_date) }}
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-btn
                v-if="item.status === 'active'"
                icon
                small
                color="success"
                @click="generateMessages(item)"
                :disabled="generatingMessages === item.id"
              >
                <v-icon>mdi-email-fast</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                @click="viewCampaign(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="warning"
                @click="editCampaign(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="deleteCampaign(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Create Campaign Dialog -->
      <v-dialog v-model="showCreateDialog" max-width="900px" persistent>
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="primary">mdi-bullhorn</v-icon>
            Create New Campaign
          </v-card-title>
          <v-card-text class="pa-6">
            <v-form ref="createForm" v-model="validForm">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="newCampaign.campaign_name"
                    label="Campaign Name"
                    outlined
                    required
                    :rules="[v => !!v || 'Campaign name is required']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCampaign.contact_name"
                    label="Contact Name"
                    outlined
                    required
                    :rules="[v => !!v || 'Contact name is required']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCampaign.contact_email"
                    label="Contact Email"
                    outlined
                    type="email"
                    required
                    :rules="emailRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCampaign.contact_company"
                    label="Contact Company"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCampaign.contact_position"
                    label="Contact Position"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newCampaign.deal_stage"
                    label="Deal Stage"
                    :items="dealStageOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Deal stage is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCampaign.deal_value"
                    label="Deal Value"
                    outlined
                    prefix="$"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newCampaign.last_interaction_type"
                    label="Last Interaction Type"
                    :items="interactionTypeOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Last interaction type is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="newCampaign.last_interaction_date"
                        label="Last Interaction Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="newCampaign.last_interaction_date"
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newCampaign.engagement_level"
                    label="Engagement Level"
                    :items="engagementOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Engagement level is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newCampaign.follow_up_tone"
                    label="Follow-up Tone"
                    :items="toneOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Follow-up tone is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="newCampaign.follow_up_frequency_days"
                    label="Follow-up Frequency (days)"
                    outlined
                    type="number"
                    min="1"
                    max="30"
                    required
                    :rules="[v => !!v || 'Follow-up frequency is required', v => v >= 1 && v <= 30 || 'Must be between 1 and 30 days']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newCampaign.deal_notes"
                    label="Deal Notes"
                    outlined
                    rows="3"
                    hint="Any relevant context about the deal"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newCampaign.last_interaction_notes"
                    label="Last Interaction Notes"
                    outlined
                    rows="3"
                    hint="Summary of your last interaction with this contact"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="closeCreateDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="creatingCampaign"
              :disabled="!validForm"
              @click="createCampaign"
            >
              Create Campaign
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "FollowUpBotAICampaigns",
  data() {
    return {
      isLoading: false,
      campaigns: [],
      showCreateDialog: false,
      dateMenu: false,
      validForm: false,
      creatingCampaign: false,
      generatingMessages: null,
      filters: {
        search: '',
        status: '',
        deal_stage: '',
        engagement_level: ''
      },
      newCampaign: {
        campaign_name: '',
        contact_name: '',
        contact_email: '',
        contact_company: '',
        contact_position: '',
        deal_stage: 'initial_contact',
        deal_value: '',
        last_interaction_type: 'email',
        last_interaction_date: '',
        engagement_level: 'medium',
        follow_up_tone: 'friendly',
        follow_up_frequency_days: 3,
        deal_notes: '',
        last_interaction_notes: ''
      },
      headers: [
        { text: 'Contact', value: 'contact_info', sortable: false },
        { text: 'Campaign', value: 'campaign_name', sortable: true },
        { text: 'Deal Stage', value: 'deal_stage', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Engagement', value: 'engagement_level', sortable: true },
        { text: 'Follow-ups', value: 'follow_ups', sortable: false },
        { text: 'Next Follow-up', value: 'next_follow_up_date', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '150px' }
      ],
      statusOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Paused', value: 'paused' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      dealStageOptions: [
        { text: 'Initial Contact', value: 'initial_contact' },
        { text: 'Demo Scheduled', value: 'demo_scheduled' },
        { text: 'Demo Completed', value: 'demo_completed' },
        { text: 'Proposal Sent', value: 'proposal_sent' },
        { text: 'Negotiation', value: 'negotiation' },
        { text: 'Procurement Review', value: 'procurement_review' },
        { text: 'Final Decision', value: 'final_decision' },
        { text: 'Closed Won', value: 'closed_won' },
        { text: 'Closed Lost', value: 'closed_lost' }
      ],
      engagementOptions: [
        { text: 'High', value: 'high' },
        { text: 'Medium', value: 'medium' },
        { text: 'Low', value: 'low' }
      ],
      toneOptions: [
        { text: 'Friendly', value: 'friendly' },
        { text: 'Urgent', value: 'urgent' },
        { text: 'Consultative', value: 'consultative' },
        { text: 'Formal', value: 'formal' }
      ],
      interactionTypeOptions: [
        { text: 'Email', value: 'email' },
        { text: 'Call', value: 'call' },
        { text: 'Meeting', value: 'meeting' },
        { text: 'Demo', value: 'demo' },
        { text: 'Presentation', value: 'presentation' },
        { text: 'Other', value: 'other' }
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ]
    };
  },
  async mounted() {
    await this.fetchCampaigns();
  },
  methods: {
    async fetchCampaigns() {
      try {
        this.isLoading = true;
        const response = await axios.get('follow-up-campaigns');
        this.campaigns = response.data.data;
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load campaigns. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async createCampaign() {
      if (!this.$refs.createForm.validate()) return;
      
      try {
        this.creatingCampaign = true;
        const response = await axios.post('follow-up-campaigns', this.newCampaign);
        this.campaigns.unshift(response.data.data);
        this.closeCreateDialog();
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Campaign created successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error creating campaign:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to create campaign. Please try again.']
        });
      } finally {
        this.creatingCampaign = false;
      }
    },
    async generateMessages(campaign) {
      try {
        this.generatingMessages = campaign.id;
        await axios.post(`follow-up-campaigns/${campaign.id}/generate`);
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Follow-up messages generated successfully!',
          color: 'success'
        });
        
        // Navigate to messages page to see generated content
        this.$router.push('/agents/followupbot-ai/messages');
      } catch (error) {
        console.error('Error generating messages:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to generate messages. Please try again.']
        });
      } finally {
        this.generatingMessages = null;
      }
    },
    async deleteCampaign(campaign) {
      if (!confirm(`Are you sure you want to delete the campaign "${campaign.campaign_name}"?`)) {
        return;
      }
      
      try {
        await axios.delete(`follow-up-campaigns/${campaign.id}`);
        const index = this.campaigns.findIndex(c => c.id === campaign.id);
        if (index !== -1) {
          this.campaigns.splice(index, 1);
        }
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Campaign deleted successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error deleting campaign:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to delete campaign. Please try again.']
        });
      }
    },
    viewCampaign(campaign) {
      this.$router.push(`/agents/followupbot-ai/campaigns/${campaign.id}`);
    },
    editCampaign(campaign) {
      this.$router.push(`/agents/followupbot-ai/campaigns/${campaign.id}/edit`);
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newCampaign = {
        campaign_name: '',
        contact_name: '',
        contact_email: '',
        contact_company: '',
        contact_position: '',
        deal_stage: 'initial_contact',
        deal_value: '',
        last_interaction_type: 'email',
        last_interaction_date: '',
        engagement_level: 'medium',
        follow_up_tone: 'friendly',
        follow_up_frequency_days: 3,
        deal_notes: '',
        last_interaction_notes: ''
      };
      this.$refs.createForm?.resetValidation();
    },
    applyFilters() {
      // Implement filtering logic if needed
    },
    getStatusColor(status) {
      const colors = {
        active: 'success',
        paused: 'warning',
        completed: 'info',
        cancelled: 'error'
      };
      return colors[status] || 'grey';
    },
    getStatusText(status) {
      const texts = {
        active: 'Active',
        paused: 'Paused', 
        completed: 'Completed',
        cancelled: 'Cancelled'
      };
      return texts[status] || status;
    },
    getDealStageColor(stage) {
      const colors = {
        initial_contact: 'blue',
        demo_scheduled: 'orange',
        demo_completed: 'purple',
        proposal_sent: 'indigo',
        negotiation: 'teal',
        procurement_review: 'cyan',
        final_decision: 'lime',
        closed_won: 'green',
        closed_lost: 'red'
      };
      return colors[stage] || 'grey';
    },
    getDealStageText(stage) {
      const texts = {
        initial_contact: 'Initial Contact',
        demo_scheduled: 'Demo Scheduled',
        demo_completed: 'Demo Completed',
        proposal_sent: 'Proposal Sent',
        negotiation: 'Negotiation',
        procurement_review: 'Procurement Review',
        final_decision: 'Final Decision',
        closed_won: 'Closed Won',
        closed_lost: 'Closed Lost'
      };
      return texts[stage] || stage;
    },
    getEngagementColor(level) {
      const colors = {
        high: '#51cf66',
        medium: '#ff9500',
        low: '#ff6b6b'
      };
      return colors[level] || '#667eea';
    },
    getEngagementIcon(level) {
      const icons = {
        high: 'mdi-trending-up',
        medium: 'mdi-trending-neutral',
        low: 'mdi-trending-down'
      };
      return icons[level] || 'mdi-trending-neutral';
    },
    getEngagementText(level) {
      const texts = {
        high: 'High',
        medium: 'Medium',
        low: 'Low'
      };
      return texts[level] || level;
    },
    formatNextFollowUp(date) {
      if (!date) return 'Not scheduled';
      const followUpDate = new Date(date);
      const now = new Date();
      const diffTime = followUpDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'Overdue';
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      return `In ${diffDays} days`;
    }
  }
};
</script>

<style scoped>
.followupbot-campaigns {
  min-height: calc(100vh - 64px);
}

.header-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.create-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.filters-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.campaigns-table {
  border-radius: 16px !important;
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.contact-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
}

.contact-details {
  flex: 1;
}

.contact-name {
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
}

.contact-company {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
}

.stage-chip, .status-chip {
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.engagement-badge {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #2c3e50;
}

.follow-up-stats {
  display: flex;
  gap: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.stat-label {
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
}

.next-followup {
  font-size: 12px;
  color: #64748b;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .contact-info {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .contact-avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
}
</style>